import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Lock, Sparkles, FileDown, CloudUpload, ImagePlus, Link2, ArrowRight, WandSparkles } from "lucide-react";
import { SECTIONS } from "@/data/organizerSchema";
import TeaserChat from "@/components/TeaserChat";
import TermsGate from "@/components/TermsGate";

const PRICE = 49;

const INCLUDED = [
  { icon: WandSparkles, title: "AI Copilot with one-tap apply", text: "Ask for taglines, hex palettes, LLC checklists, bios or pricing. Tap “Apply to my organizer” and the answer drops straight into the right fields." },
  { icon: ImagePlus, title: "Logo upload", text: "Upload your logo (PNG, JPG, SVG or WEBP). It lives inside your branding section and prints on the cover of your PDF." },
  { icon: CloudUpload, title: "Autosave & come back anytime", text: "Every keystroke saves to your account. Log in from any device and pick up exactly where you left off — for life." },
  { icon: FileDown, title: "Full PDF blueprint + Brand Kit", text: "One click compiles all 8 sections into a printable business blueprint, and a second click produces a one-page brand card — logo, palette and fonts — to hand to any designer." },
  { icon: Link2, title: "Curated setup links", text: "Direct links to the IRS EIN application, SBA registration, GoDaddy, Namecheap, Google Fonts, Instagram, TikTok for Business, Stripe and more — right where you need them." },
  { icon: Check, title: "Progress tracking", text: "A live completion bar and per-section checkmarks so you always know what's done and what's next." },
];

const FAQ = [
  { q: "Is this a subscription?", a: `No. It's a single one-time payment of $${PRICE}. You keep access to your organizer, the copilot and PDF export for as long as the site exists.` },
  { q: "Does the AI copilot cost extra?", a: "Never. The built-in AI copilot is included with your one-time purchase and stays unlocked for life — no credits, no add-ons, no monthly fee." },
  { q: "Do I need any technical skills?", a: "None. Every field has a plain-English label, a placeholder example, and a copilot that can draft it for you." },
  { q: "Can I use it for more than one idea?", a: "Your account holds one living organizer, and you can rewrite it as your business evolves. Export a PDF snapshot any time before making changes." },
  { q: "Is my information private?", a: "Your organizer is tied to your username and password and is only visible to you. The copilot reads it solely to personalize your answers." },
];

const TESTIMONIALS = [
  { quote: "I had my LLC steps, my palette and my Instagram bio done in one evening. It felt like having a creative director on call.", name: "Maya R.", role: "Lash studio owner" },
  { quote: "The brand kit alone saved me a week of back-and-forth with my designer.", name: "Devon K.", role: "Personal trainer" },
  { quote: "Finally one place for every decision. I open it before every meeting.", name: "Priya S.", role: "Boutique founder" },
];

function TestimonialStrip() {
  return (
    <div className="mt-6 flex flex-col gap-4" data-testid="testimonial-strip">
      {TESTIMONIALS.map((t) => (
        <figure key={t.name} className="border-l border-[#E8DCCB] pl-4" data-testid="testimonial">
          <blockquote className="italic text-[14px] leading-relaxed text-[#57473B]">“{t.quote}”</blockquote>
          <figcaption className="f-mono text-[10px] text-[#A8785A] mt-1.5">{t.name} · <span className="text-[#B9A896]">{t.role}</span></figcaption>
        </figure>
      ))}
    </div>
  );
}

function BuyCard({ user, onLogin, compact, onBuy }) {
  const buy = () => {
    if (!user) return onLogin();
    onBuy({ lookup_key: "organizer_lifetime", name: "The Organizer — Lifetime Access", price: `$${PRICE}`, billing: "one-time" });
  };
  return (
    <div className={`glass rounded-2xl ${compact ? "p-6" : "p-8"}`} data-testid="buy-card">
      <div className="flex items-baseline gap-2">
        <span className="f-display text-5xl font-bold text-[#A8785A]">${PRICE}</span>
        <span className="f-mono text-[11px] text-[#8C7562]">ONE-TIME · LIFETIME</span>
      </div>
      <p className="text-sm text-[#8C7562] mt-3 leading-relaxed">
        Instant access after checkout. No monthly fees, no upsells inside.
      </p>
      <button onClick={buy} className="btn-primary w-full justify-center mt-6" data-testid="buy-organizer-btn">
        <Lock size={15} /> {user ? "Unlock The Organizer" : "Log In to Purchase"}
      </button>
      {!user && (
        <button onClick={onLogin} className="w-full text-center text-xs text-[#8C7562] mt-4 hover:text-[#3A2E25] transition-colors" data-testid="buy-have-account-btn">
          Already purchased? Log in to open your workspace
        </button>
      )}
      <ul className="mt-6 flex flex-col gap-2.5">
        {["9 guided sections · 38 fields + a rich Notes page", "AI copilot + one-tap apply — included for life", "Logo upload + one-page brand kit", "Unlimited PDF exports", "Cloud autosave, any device"].map((t) => (
          <li key={t} className="flex items-center gap-2.5 text-sm text-[#57473B]"><Check size={14} className="text-[#C48A9F] shrink-0" /> {t}</li>
        ))}
      </ul>
    </div>
  );
}

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function OrganizerSales({ user, onLogin }) {
  const totalFields = SECTIONS.reduce((n, s) => n + s.fields.length, 0);
  const [pendingPlan, setPendingPlan] = useState(null);
  return (
    <main className="min-h-screen pt-32 pb-28 vector-grid" data-testid="organizer-sales">
      <TermsGate plan={pendingPlan} onClose={() => setPendingPlan(null)} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[1.35fr_1fr] gap-12 items-start">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="eyebrow">The Organizer · one-time purchase</span>
            <h1 className="f-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mt-4 leading-[1.02]">
              Everything your business needs to exist — <span className="f-serif text-[#A8785A]">in one place.</span>
            </h1>
            <p className="text-[#8C7562] text-base leading-relaxed max-w-2xl mt-7">
              The Organizer is a guided workspace that walks you through the {SECTIONS.length} decisions every new or growing business has to make —
              your name and mission, your LLC and EIN, your domain, your logo and palette, your socials, your offers, your money and your
              next 90 days. Fill it in yourself, or let the built-in AI copilot draft it with you and apply its answers in one tap. When you're done,
              download the whole thing as a polished PDF blueprint.
            </p>
            {user && (
              <p className="f-mono text-[11px] text-[#A8785A] mt-6" data-testid="sales-logged-in-note">
                LOGGED IN AS @{user.username} · UNLOCK BELOW TO OPEN YOUR WORKSPACE
              </p>
            )}
            <div className="flex flex-wrap gap-4 mt-9">
              <a href="#whats-inside" className="btn-ghost" data-testid="sales-see-inside-btn">See what's inside <ArrowRight size={15} /></a>
              <a href="#try-copilot" className="btn-ghost" data-testid="sales-try-copilot-btn"><Sparkles size={15} className="text-[#D4A0B4]" /> Try the copilot free</a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:sticky lg:top-28">
            <BuyCard user={user} onLogin={onLogin} onBuy={setPendingPlan} />
            <TestimonialStrip />
          </motion.div>
        </div>

        <section id="whats-inside" className="mt-28 scroll-mt-28" data-testid="sales-whats-inside">
          <motion.p {...fadeUp} className="eyebrow mb-4">What's inside</motion.p>
          <motion.h2 {...fadeUp} className="f-display text-3xl sm:text-4xl font-medium tracking-tight mb-4">
            {SECTIONS.length} sections. {totalFields} fields. Zero guesswork.
          </motion.h2>
          <motion.p {...fadeUp} className="text-[#8C7562] text-sm max-w-2xl mb-12 leading-relaxed">
            Every field below is included, with an example placeholder and quick links to the exact websites you need to complete it.
          </motion.p>
          <div className="grid md:grid-cols-2 gap-5">
            {SECTIONS.map((s, i) => (
              <motion.div key={s.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 2) * 0.08 }}
                className={`rounded-2xl border border-[#E8DCCB] p-7 ${i % 3 === 1 ? "bg-[#FFF3F8]" : "bg-[#FFFDF7]"}`}
                data-testid={`sales-section-${s.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="f-mono text-[10px] text-[#A8785A]">SECTION {String(i + 1).padStart(2, "0")}</span>
                  <span className="f-mono text-[10px] text-[#B9A896]">{s.fields.length} FIELDS</span>
                </div>
                <h3 className="f-display text-xl font-medium tracking-tight">{s.title}</h3>
                <p className="text-sm text-[#8C7562] mt-1.5 mb-5">{s.blurb}</p>
                <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
                  {s.fields.map((f) => (
                    <li key={f.key} className="flex items-start gap-2 text-[13px] text-[#57473B]">
                      <Check size={13} className="text-[#C48A9F] mt-0.5 shrink-0" /> {f.label}
                    </li>
                  ))}
                </ul>
                {s.links && (
                  <p className="f-mono text-[10px] text-[#B9A896] mt-5 leading-relaxed">
                    LINKS · {s.links.map((l) => l.label).join(" · ")}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        <section className="mt-28" data-testid="sales-included">
          <motion.p {...fadeUp} className="eyebrow mb-4">Also included</motion.p>
          <motion.h2 {...fadeUp} className="f-display text-3xl sm:text-4xl font-medium tracking-tight mb-12">Tools that do the heavy lifting</motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INCLUDED.map((f, i) => (
              <motion.div key={f.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 3) * 0.08 }}
                className="glass rounded-2xl p-7 group hover:border-[#D4A0B4] transition-colors" data-testid={`sales-feature-${i}`}>
                <span className="w-11 h-11 rounded-xl bg-[#FFF3F8] border border-[#E8DCCB] flex items-center justify-center mb-5 group-hover:bg-[#A8785A] transition-colors">
                  <f.icon size={19} className="text-[#A8785A] group-hover:text-[#FFFDF7] transition-colors" />
                </span>
                <h3 className="f-display font-medium text-lg tracking-tight mb-2">{f.title}</h3>
                <p className="text-sm text-[#8C7562] leading-relaxed">{f.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section id="try-copilot" className="mt-28 scroll-mt-28 grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start" data-testid="sales-teaser">
          <motion.div {...fadeUp}>
            <span className="eyebrow">Free preview</span>
            <h2 className="f-display text-3xl sm:text-4xl font-medium tracking-tight mt-4 leading-[1.05]">
              Ask the copilot <span className="f-serif text-[#A8785A]">three questions</span>, on us.
            </h2>
            <p className="text-sm text-[#8C7562] leading-relaxed mt-5 max-w-md">
              This is the same Claude-powered copilot that lives inside The Organizer. Ask for a tagline, a palette or an LLC checklist.
              Inside the paid workspace it also reads your organizer and applies answers into your fields with one tap.
            </p>
          </motion.div>
          <motion.div {...fadeUp}><TeaserChat /></motion.div>
        </section>

        <section className="mt-28 grid lg:grid-cols-[1fr_1fr] gap-12" data-testid="sales-faq">
          <div>
            <motion.p {...fadeUp} className="eyebrow mb-4">Questions</motion.p>
            <motion.h2 {...fadeUp} className="f-display text-3xl sm:text-4xl font-medium tracking-tight mb-10">Before you unlock</motion.h2>
            <div className="flex flex-col">
              {FAQ.map((f) => (
                <motion.div key={f.q} {...fadeUp} className="py-6 border-b border-[#E8DCCB]">
                  <h3 className="font-bold text-base">{f.q}</h3>
                  <p className="text-sm text-[#8C7562] leading-relaxed mt-2">{f.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.div {...fadeUp} className="lg:pt-24">
            <BuyCard user={user} onLogin={onLogin} onBuy={setPendingPlan} compact />
          </motion.div>
        </section>
      </div>
    </main>
  );
}
